// Orquestrador da rota /c (PWA de captura). Máquina de estados:
//   repouso --gravar--> (gravando/pausado, via useGravador) --encerrar--> enviando
//   repouso --enviar arquivo--------------------------------> enviando
//   enviando --201--> codigo   |   enviando --erro--> erro
//   codigo/erro --reenviar--> enviando (reusa o Blob guardado)
//
// O Blob fica em blobRef até "novo atendimento": permite "gerar outro código" e
// "tentar de novo" sem regravar. O celular só conhece o pairingCode — nada do conteúdo.

import { useEffect, useRef, useState } from 'react';

import { enviarAudio } from '../../api/upload';
import { corC } from '../../styles/tokensCelular';
import { useGravador } from './useGravador';
import { Repouso } from './Repouso';
import { Gravando } from './Gravando';
import { Enviando } from './Enviando';
import { CodigoPronto } from './CodigoPronto';
import { ErroEnvio } from './ErroEnvio';

type Tela =
  | { t: 'inicio' } // repouso ou gravando — quem manda é a fase do gravador
  | { t: 'enviando'; ratio: number; indeterminado: boolean }
  | { t: 'codigo'; codigo: string }
  | { t: 'erro'; motivo: string };

export function CapturaPage(): JSX.Element {
  const grav = useGravador();
  const [tela, setTela] = useState<Tela>({ t: 'inicio' });
  const blobRef = useRef<Blob | null>(null);

  // A rota /c compartilha o index.html do editor (viewport=1366, fundo claro). Ajusta
  // para device-width e fundo escuro só enquanto esta tela vive; restaura ao sair.
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    const anterior = meta?.getAttribute('content') ?? null;
    meta?.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover');
    const bodyBg = document.body.style.background;
    document.body.style.background = corC.fundoExterno;
    return () => {
      if (meta && anterior !== null) meta.setAttribute('content', anterior);
      document.body.style.background = bodyBg;
    };
  }, []);

  async function enviar(blob: Blob): Promise<void> {
    blobRef.current = blob;
    setTela({ t: 'enviando', ratio: 0, indeterminado: false });
    const r = await enviarAudio(blob, (enviados, total) => {
      setTela({
        t: 'enviando',
        ratio: total > 0 ? enviados / total : 0,
        indeterminado: total === 0,
      });
    });
    if (r.ok) setTela({ t: 'codigo', codigo: r.pairingCode });
    else setTela({ t: 'erro', motivo: r.motivo });
  }

  async function encerrarEEnviar(): Promise<void> {
    const blob = await grav.encerrar();
    if (blob) void enviar(blob);
    else setTela({ t: 'erro', motivo: 'Nada foi gravado. Toque em gravar e tente de novo.' });
  }

  function novoAtendimento(): void {
    blobRef.current = null;
    grav.descartar();
    setTela({ t: 'inicio' });
  }

  let conteudo: JSX.Element;
  if (tela.t === 'enviando') {
    conteudo = <Enviando ratio={tela.ratio} indeterminado={tela.indeterminado} />;
  } else if (tela.t === 'codigo') {
    conteudo = (
      <CodigoPronto
        codigo={tela.codigo}
        onNovo={novoAtendimento}
        onGerarOutro={() => {
          if (blobRef.current) void enviar(blobRef.current);
        }}
      />
    );
  } else if (tela.t === 'erro') {
    conteudo = (
      <ErroEnvio
        motivo={tela.motivo}
        onTentar={() => {
          if (blobRef.current) void enviar(blobRef.current);
          else novoAtendimento();
        }}
        onDescartar={novoAtendimento}
      />
    );
  } else if (grav.fase !== 'repouso') {
    conteudo = (
      <Gravando
        fase={grav.fase}
        segundos={grav.segundos}
        interrompido={grav.interrompido}
        analyser={grav.analyser}
        onPausar={grav.pausar}
        onRetomar={grav.retomar}
        onEncerrar={() => void encerrarEEnviar()}
      />
    );
  } else {
    conteudo = (
      <Repouso
        onGravar={() => void grav.iniciar()}
        onArquivo={(f) => void enviar(f)}
        erro={grav.erroMic}
      />
    );
  }

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: corC.fundoExterno,
        display: 'grid',
        placeItems: 'center',
        fontFamily: 'Barlow, system-ui, sans-serif',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          minHeight: '100dvh',
          background: corC.fundo,
          color: corC.texto,
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {conteudo}
      </div>
    </div>
  );
}
